// Gemeinsame Bausteine der Teamverwaltung (design_handoff_team §2–§3): Kopf, Bereichs-Segment, Avatar,
// Rollen-/Status-Chip und die Zustands-Karten (Laden, Fehler, Leer).
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { CircleNotch, Eye, HardHat, ShieldCheck, Users, UsersThree, WarningCircle, type Icon } from '@phosphor-icons/react';
import { avatarHintergrund, initialen, istAktiv, rollenLabel, statusText } from '../../../../lib/team/mitarbeiter';

export type TeamTab = 'mitarbeiter' | 'teams' | 'sichtbarkeit';

const TABS: { key: TeamTab; label: string; icon: Icon; tip: string }[] = [
  { key: 'mitarbeiter', label: 'Mitarbeiter', icon: Users, tip: 'Mitarbeiter – Zugänge, Rollen und Berechtigungen' },
  { key: 'teams', label: 'Teams', icon: UsersThree, tip: 'Teams – Gruppen mit gemeinsamem Zugriff und Teamleitung' },
  { key: 'sichtbarkeit', label: 'Sichtbarkeit', icon: Eye, tip: 'Sichtbarkeit – Wer sieht alle Tickets und Rechnungen?' },
];

/** Seitenkopf: Überzeile, Titel, Untertitel; rechts der Primär-Knopf des Tabs. */
export function TeamKopf({ mobil, schmal, aktion }: { mobil: boolean; schmal: boolean; aktion?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Team</span>
        <h1 className="m-0" style={{ fontSize: schmal || mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02 }}>Teamverwaltung</h1>
        <span style={{ fontSize: 14, color: 'var(--ink2)' }}>Management der Team-Mitarbeiter</span>
      </div>
      {aktion}
    </header>
  );
}

/** Bereichs-Segment (Glas-Pille, aktiv = Akzent-Füllung) mit Anzahlen; mobil horizontal scrollbar. */
export function TeamSegment({ wert, onChange, anzahl, mobil }: { wert: TeamTab; onChange: (t: TeamTab) => void; anzahl: Partial<Record<TeamTab, number>>; mobil: boolean }) {
  const leiste = useRef<HTMLElement>(null);
  // Mobil scrollt das Segment waagerecht – der aktive Tab bleibt im Bild.
  useEffect(() => {
    const aktiv = leiste.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (aktiv && leiste.current) leiste.current.scrollTo({ left: aktiv.offsetLeft - 4 - (leiste.current.clientWidth - aktiv.offsetWidth) / 2, behavior: 'smooth' });
  }, [wert]);
  const tastatur = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = TABS.findIndex((t) => t.key === wert);
    const n = TABS[(i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length];
    onChange(n.key);
    requestAnimationFrame(() => leiste.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus());
  };
  return (
    <nav ref={leiste} aria-label="Team-Bereiche" role="tablist" onKeyDown={tastatur} className="immo-glas flex self-start overflow-x-auto"
      style={{ gap: 4, padding: 4, borderRadius: 20, maxWidth: '100%', scrollbarWidth: 'none' }}>
      {TABS.map((t) => {
        const an = t.key === wert;
        const I = t.icon;
        const n = anzahl[t.key];
        return (
          <button key={t.key} type="button" role="tab" aria-selected={an} tabIndex={an ? 0 : -1} data-tip={t.tip} onClick={() => onChange(t.key)}
            className="inline-flex shrink-0 items-center whitespace-nowrap"
            style={{ height: mobil ? 44 : 40, padding: '0 16px', gap: 8, borderRadius: 16, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 13.5, fontWeight: 800, background: an ? 'var(--acc-fill)' : 'transparent', color: an ? 'var(--acc-ink)' : 'var(--ink2)',
              boxShadow: an ? 'var(--acc-sh)' : 'none', transition: 'background .2s, color .2s' }}>
            <I size={17} weight={an ? 'fill' : 'bold'} />{t.label}
            {n !== undefined && (
              <span className="inline-flex items-center justify-center tabular-nums"
                style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, fontSize: 11, fontWeight: 800, background: an ? 'rgba(0,0,0,0.12)' : 'var(--fill2)' }}>{n}</span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

/** Avatar: Foto, sonst Initialen auf Farbverlauf; optional Status-Punkt. */
export function MitarbeiterAvatar({ id, name, url, groesse, punkt }: { id: string; name: string; url?: string | null; groesse: number; punkt?: string }) {
  const [kaputt, setKaputt] = useState(false);
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: groesse, height: groesse, borderRadius: '50%', fontSize: Math.round(groesse * 0.33), fontWeight: 800, color: '#fff',
        background: avatarHintergrund(id), boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35)' }}>
      {url && !kaputt
        ? <img src={url} alt={name} onError={() => setKaputt(true)} className="h-full w-full object-cover" style={{ borderRadius: '50%' }} />
        : initialen(name)}
      {punkt && (
        <span aria-hidden style={{ position: 'absolute', right: 0, bottom: 0, width: Math.max(11, groesse * 0.26), height: Math.max(11, groesse * 0.26),
          borderRadius: '50%', background: punkt, boxShadow: '0 0 0 2.5px var(--sheet)' }} />
      )}
    </span>
  );
}

const ROLLEN_STIL: Record<string, { icon: Icon; fg: string; bg: string }> = {
  admin: { icon: ShieldCheck, fg: 'var(--acc)', bg: 'var(--acc-soft)' },
  team: { icon: Users, fg: 'var(--info)', bg: 'var(--info-soft)' },
  hausmeister: { icon: HardHat, fg: 'var(--warn)', bg: 'var(--warn-soft)' },
};
export const rollenStil = (role: string) => ROLLEN_STIL[role] ?? ROLLEN_STIL.team;

/** Rolle-Chip der Tabelle („Administrator“ / „Mitarbeiter“). */
export function RollenChip({ role }: { role: string }) {
  const s = rollenStil(role);
  const I = s.icon;
  return (
    <span className="inline-flex items-center justify-self-start whitespace-nowrap"
      style={{ height: 26, padding: '0 11px', gap: 6, borderRadius: 13, fontSize: 12, fontWeight: 800, background: s.bg, color: s.fg }}>
      <I size={13} weight="fill" />{rollenLabel(role, true)}
    </span>
  );
}

export const statusFarbe = (status: string | null | undefined) =>
  istAktiv(status) ? { fg: 'var(--ok)', bg: 'var(--ok-soft)' } : { fg: 'var(--ink3)', bg: 'var(--fill2)' };

/** Status-Chip (Aktiv grün, sonst grau); `punkt` = mit Farbpunkt. */
export function StatusChip({ status, punkt = false }: { status: string | null | undefined; punkt?: boolean }) {
  const f = statusFarbe(status);
  return (
    <span className="inline-flex shrink-0 items-center justify-self-start whitespace-nowrap"
      style={{ height: 26, padding: '0 11px', gap: 6, borderRadius: 13, fontSize: 12, fontWeight: 800, background: f.bg, color: f.fg }}>
      {punkt && <span style={{ width: 7, height: 7, borderRadius: '50%', background: f.fg }} />}
      {statusText(status)}
    </span>
  );
}

/** Lade-Karte. */
export function LadeKarte({ text }: { text?: string }) {
  return (
    <div className="immo-glas flex flex-col items-center" style={{ borderRadius: 26, padding: '60px 20px', gap: 14 }}>
      <CircleNotch size={34} weight="bold" className="animate-spin" color="var(--acc)" />
      {text && <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink2)' }}>{text}</span>}
    </div>
  );
}

/** Fehler-Karte mit „Erneut versuchen“. */
export function FehlerKarte({ meldung, onErneut }: { meldung: string; onErneut?: () => void }) {
  return (
    <div role="alert" className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '40px 20px', gap: 10 }}>
      <span className="flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--bad-soft)' }}>
        <WarningCircle size={28} weight="fill" color="var(--bad)" />
      </span>
      <span style={{ fontSize: 17, fontWeight: 800 }}>Fehler beim Abrufen der Daten</span>
      <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{meldung}</span>
      {onErneut && (
        <button type="button" onClick={onErneut} data-tip="Erneut versuchen – Daten neu laden"
          style={{ marginTop: 6, height: 42, padding: '0 18px', border: 'none', borderRadius: 21, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
          Erneut versuchen
        </button>
      )}
    </div>
  );
}

/** Leer-Karte (Icon, Titel, optional Aktion). */
export function LeerKarte({ icon: I, titel, children }: { icon: Icon; titel: string; children?: ReactNode }) {
  return (
    <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: '44px 20px', gap: 10 }}>
      <span className="flex items-center justify-center" style={{ width: 60, height: 60, borderRadius: 20, background: 'var(--fill2)' }}>
        <I size={30} weight="fill" color="var(--ink3)" />
      </span>
      <span style={{ fontSize: 16, fontWeight: 800 }}>{titel}</span>
      {children}
    </div>
  );
}

/** Eingabefeld-Rahmen (48 px, --fill2) mit Akzent-Ring, solange darin etwas den Fokus hat – Dialoge liegen außerhalb
 *  von .immo-flaeche und bekommen den globalen Fokus-Ring nicht; die Felder selbst haben keine Outline. */
export function FeldRahmen({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const [fokus, setFokus] = useState(false);
  return (
    <span className="flex items-center" onFocus={() => setFokus(true)} onBlur={() => setFokus(false)}
      style={{ height: 48, borderRadius: 14, gap: 10, padding: '0 14px', background: 'var(--fill2)',
        boxShadow: fokus ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)', transition: 'box-shadow .15s', ...style }}>
      {children}
    </span>
  );
}
